import { List } from '@mui/material';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { DocumentUpload, type DocumentUploadType } from '~/components/FileUpload/DocumentUpload/DocumentUpload';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { DocumentListItem } from '~/modules/demand/DemandDocumentWidget/components/DemandDocumentForm/components/DocumentListItem';
import { createProgressIndicatorAlternatingProps } from '~/modules/demand/DemandDocumentWidget/components/DemandDocumentForm/components/DocumentListItem.util';
import { DemandDocumentFormData } from '~/modules/demand/DemandDocumentWidget/components/DemandDocumentForm/DemandDocumentForm.schema';

const FILE_ACCEPTS = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.oasis.opendocument.text': ['.odt'],
  'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.oasis.opendocument.presentation': ['.odp'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/webp': ['.webp'],
  'image/tiff': ['.tiff', '.tif'],
  'image/svg+xml': ['.svg'],
};
export interface DemandDocumentContentProps {
  loading?: boolean;
}

export const DemandDocumentForm = ({ loading }: DemandDocumentContentProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandDocumentWidget.uploadStep' });
  const { onError } = useCustomSnackbar();

  const form = useFormContext<DemandDocumentFormData>();
  const { watch } = form;

  const { fields, append } = useFieldArray<DemandDocumentFormData>({
    name: 'files',
  });

  const watchedFiles = watch('files');

  const onUploadSuccess: DocumentUploadType['onUploadSuccess'] = async (data) => {
    append({
      fileId: data.uploadId,
      isSharedWithSupplier: false,
      isDeleted: false,
      hasNegotiationCase: false,
    });
  };

  return (
    <>
      {loading ? (
        <PlaceholderWidget />
      ) : (
        <>
          <DocumentUpload
            buttonLabel={t('upload.buttonLabel')}
            onUploadSuccess={onUploadSuccess}
            onUploadFailed={onError}
            uploadIndicatorProps={createProgressIndicatorAlternatingProps(t)}
            accept={FILE_ACCEPTS}
          />
          <List disablePadding>
            {fields.map((file, index) => (
              <DocumentListItem
                key={file.fileId}
                file={file}
                index={index}
                isMarkedForDeletion={watchedFiles[index].isDeleted === true}
              />
            ))}
          </List>
        </>
      )}
    </>
  );
};
